import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, RefreshCw } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { PerformanceScopeBanner } from '@/components/PerformanceScopeBanner'
import { usePerformanceScope } from '@/lib/use-performance-scope'

interface CancellationRow {
  id: string
  showId: string
  reason: string
  notes?: string
  insurancePolicyId?: string
  cancellationFeePence?: number
  refundsIssued: boolean
  createdAt: string
  show?: { title: string; date: string }
}

export function CancellationPage() {
  const qc = useQueryClient()
  const scope = usePerformanceScope()
  const [adding, setAdding] = useState(false)
  const { data, error, isLoading } = useQuery<CancellationRow[], ApiClientError>({
    queryKey: ['cancellations', ...scope.queryKeySuffix],
    queryFn: () => artistFetch<CancellationRow[]>(`/cancellations${scope.toQueryString()}`),
  })
  const rows = scope.filterByShowIds(data ?? [], (r) => r.showId)

  async function issueRefunds(id: string) {
    await artistFetch(`/cancellations/${id}/issue-refunds`, { method: 'POST' })
    qc.invalidateQueries({ queryKey: ['cancellations'] })
  }

  return (
    <div>
      <PageHeader
        title="Cancellations"
        subtitle="Insurance tracking, cancellation fees, and fan refund status."
        actions={
          <button
            type="button"
            onClick={() => setAdding((a) => !a)}
            className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
          >
            <Plus className="h-4 w-4" />
            {adding ? 'Cancel' : 'Add cancellation'}
          </button>
        }
      />
      <PerformanceScopeBanner />
      {adding && (
        <AddCancellationForm
          onCreated={() => {
            setAdding(false)
            qc.invalidateQueries({ queryKey: ['cancellations'] })
          }}
        />
      )}
      <AsyncBoundary isLoading={isLoading} error={error}>
        <DataTable
          rows={rows}
          rowKey={(r) => r.id}
          emptyMessage="No cancellations recorded."
          columns={[
            {
              key: 'show',
              header: 'Show',
              render: (r) => r.show?.title ?? r.showId.slice(0, 8),
            },
            {
              key: 'reason',
              header: 'Reason',
              render: (r) => (
                <span className="capitalize">{r.reason.replace(/_/g, ' ')}</span>
              ),
            },
            {
              key: 'fee',
              header: 'Fee',
              align: 'right',
              render: (r) =>
                r.cancellationFeePence != null
                  ? `£${(r.cancellationFeePence / 100).toFixed(2)}`
                  : '—',
            },
            {
              key: 'insurance',
              header: 'Insurance',
              render: (r) => r.insurancePolicyId ? 'Covered' : 'None',
            },
            {
              key: 'refunds',
              header: 'Refunds',
              render: (r) =>
                r.refundsIssued ? (
                  <span className="text-green-700">Issued</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => issueRefunds(r.id)}
                    className="flex items-center gap-1 text-sm text-blue-700 hover:underline"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Issue
                  </button>
                ),
            },
            {
              key: 'date',
              header: 'Created',
              render: (r) => new Date(r.createdAt).toLocaleDateString(),
            },
          ]}
        />
      </AsyncBoundary>
    </div>
  )
}

function AddCancellationForm({ onCreated }: { onCreated: () => void }) {
  const [showId, setShowId] = useState('')
  const [reason, setReason] = useState('other')
  const [notes, setNotes] = useState('')
  const [insurancePolicyId, setInsurancePolicyId] = useState('')
  const [cancellationFeePence, setCancellationFeePence] = useState('')
  const [error, setErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      await artistFetch('/cancellations', {
        method: 'POST',
        body: {
          showId,
          reason,
          notes: notes || undefined,
          insurancePolicyId: insurancePolicyId || undefined,
          cancellationFeePence: cancellationFeePence ? Number(cancellationFeePence) : undefined,
        },
      })
      onCreated()
    } catch (err) {
      setErr(err instanceof ApiClientError ? err.message : 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="mb-6 space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Show ID</label>
          <input required value={showId} onChange={(e) => setShowId(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="UUID of the show" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Reason</label>
          <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm">
            <option value="force_majeure">Force majeure</option>
            <option value="artist_illness">Artist illness</option>
            <option value="promoter_default">Promoter default</option>
            <option value="low_sales">Low sales</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Cancellation fee (pence)</label>
          <input type="number" min={0} value={cancellationFeePence} onChange={(e) => setCancellationFeePence(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Insurance policy ID</label>
          <input value={insurancePolicyId} onChange={(e) => setInsurancePolicyId(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Notes</label>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <button type="submit" disabled={submitting} className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50">
        {submitting ? 'Creating...' : 'Create cancellation'}
      </button>
    </form>
  )
}
